import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { graph, theCase } from '@/data';
import type { GraphNode, GraphEdge } from '@/data';
import { Badge } from '@/components/ui/Badge';
import { Btn } from '@/components/ui/Button';
import { SectionCard } from '@/components/ui/Card';
import { PageHeader } from '@/components/layout/PageHeader';

type NodeType = GraphNode['type'];

interface NodeStyle {
  fill: string;
  stroke: string;
  r: number;
  icon?: string;
}

const nodeStyle: Record<NodeType, NodeStyle> = {
  case:      { fill: '#0054A6', stroke: '#0A2F5C', r: 34, icon: '' },
  person:    { fill: '#FFFFFF', stroke: '#0054A6', r: 26 },
  org:       { fill: '#ED1B2F', stroke: '#A50F1D', r: 30 },
  substance: { fill: '#FFCB05', stroke: '#B8920A', r: 26 },
  place:     { fill: '#EFF2F7', stroke: '#707070', r: 24 },
};

export default function Network() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { nodes, edges } = graph;
  const [selected, setSelected] = useState<string>('case');
  const ln = (ko: string, mn: string) => (lang === 'ko' ? ko : mn);

  const byId: Record<string, GraphNode> = {};
  nodes.forEach((n) => (byId[n.id] = n));
  const sel = byId[selected];
  const selEdges = edges.filter((e: GraphEdge) => e.from === selected || e.to === selected);

  function edgeMid(e: GraphEdge): { x: number; y: number } {
    const a = byId[e.from];
    const b = byId[e.to];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  const legendItems: [NodeType, string][] = [
    ['case',      t('nodeCase')],
    ['person',    t('nodePerson')],
    ['org',       t('nodeOrg')],
    ['substance', t('nodeSubstance')],
    ['place',     t('nodePlace')],
  ];

  return (
    <div className="mp-page" data-screen-label="관계 분석">
      <PageHeader
        crumbs={[t('home'), t('mAnalysis'), t('mRelation')]}
        title={t('relationTitle')}
        titleExtra={<div className="mp-pagehead__meta"><span className="mp-pagehead__updated">{t('relationSub')}</span></div>}
        actions={
          <Fragment>
            <Btn icon="bi-plus-lg">{t('addRelation')}</Btn>
            <Btn icon="bi-download">{t('exportImage')}</Btn>
          </Fragment>
        }
      />

      <div className="mp-filterbar">
        <label className="mp-filter">
          <span>{t('targetCase')}</span>
          <select defaultValue="0">
            <option value="0">ХД-2026-0142 — {ln(theCase.nameKo, theCase.nameMn)}</option>
            <option value="1">ХД-2026-0137</option>
            <option value="2">ХД-2026-0129</option>
          </select>
        </label>
        <label className="mp-filter">
          <span>{t('depth')}</span>
          <select defaultValue="2">
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
        </label>
        <Btn kind="primary" small icon="bi-search">{t('search')}</Btn>
        <span className="mp-filterbar__hint"><i className="bi bi-info-circle"></i> {t('clickNodeHint')}</span>
      </div>

      <div className="mp-grid mp-grid--graph">
        <div className="mp-card mp-graphwrap">
          <svg viewBox="0 0 920 460" className="mp-graph" role="img" aria-label={t('relationTitle')}>
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 1 L 9 5 L 0 9 z" fill="#9AA5B1"></path>
              </marker>
            </defs>
            {edges.map((e: GraphEdge, i: number) => {
              const a = byId[e.from];
              const b = byId[e.to];
              const hot = e.from === selected || e.to === selected;
              const m = edgeMid(e);
              return (
                <g key={i}>
                  <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={hot ? '#0054A6' : '#C3CBD5'}
                    strokeWidth={hot ? 2 : 1.2}
                    strokeDasharray={e.dashed ? '5 4' : 'none'}
                    markerEnd="url(#arrow)"></line>
                  <rect x={m.x - 34} y={m.y - 9} width="68" height="16" rx="2"
                    fill="#FFFFFF" stroke={hot ? '#0054A6' : '#E0E5EB'} strokeWidth="1"></rect>
                  <text x={m.x} y={m.y + 3} textAnchor="middle"
                    fontSize="9.5" fill={hot ? '#0054A6' : '#707070'}>{t(e.rel)}</text>
                </g>
              );
            })}
            {nodes.map((n: GraphNode) => {
              const s = nodeStyle[n.type];
              const isSel = n.id === selected;
              return (
                <g key={n.id} className="mp-graph__node" onClick={() => setSelected(n.id)} style={{ cursor: 'pointer' }}>
                  {isSel ? <circle cx={n.x} cy={n.y} r={s.r + 6} fill="none" stroke="#FFCB05" strokeWidth="3"></circle> : null}
                  <circle cx={n.x} cy={n.y} r={s.r} fill={s.fill} stroke={s.stroke} strokeWidth="2"></circle>
                  <text x={n.x} y={n.y + (n.type === 'person' ? 4 : 4)} textAnchor="middle"
                    fontSize={n.type === 'case' ? 11 : 10}
                    fontWeight="600"
                    fill={n.type === 'person' || n.type === 'place' || n.type === 'substance' ? '#313131' : '#FFFFFF'}>
                    {n.type === 'person' ? n.label.split('.')[1] || n.label : (n.type === 'case' ? n.label : '')}
                  </text>
                  {n.type !== 'person' && n.type !== 'case' ? (
                    <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize="9" fontWeight="600"
                      fill={n.type === 'org' ? '#fff' : '#313131'}>
                      {n.type === 'org' ? 'ORG' : n.type === 'substance' ? 'DRUG' : 'GEO'}
                    </text>
                  ) : null}
                  <text x={n.x} y={n.y + s.r + 14} textAnchor="middle" fontSize="10.5" fill="#313131" fontWeight="500">
                    {n.label}
                  </text>
                  {n.sub ? (
                    <text x={n.x} y={n.y + s.r + 26} textAnchor="middle" fontSize="9.5" fill="#707070">
                      {t(n.sub)}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>
          <div className="mp-graph__legend">
            <span className="mp-graph__legendtitle">{t('legend')}</span>
            {legendItems.map(([type, label]) => (
              <span key={type} className="mp-graph__legenditem">
                <span className="mp-graph__dot" style={{ background: nodeStyle[type].fill, borderColor: nodeStyle[type].stroke }}></span>
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="mp-stack">
          <SectionCard title={t('selectedNode')}>
            <div className="mp-nodedetail">
              <div className="mp-nodedetail__type">
                <span className="mp-graph__dot" style={{ background: nodeStyle[sel.type].fill, borderColor: nodeStyle[sel.type].stroke }}></span>
                {t('node' + sel.type.charAt(0).toUpperCase() + sel.type.slice(1))}
              </div>
              <div className="mp-nodedetail__name">{sel.label}</div>
              {sel.sub ? <Badge tone="info">{t(sel.sub)}</Badge> : null}
            </div>
          </SectionCard>
          <SectionCard title={t('connections') + ' (' + selEdges.length + ')'} flush>
            <ul className="mp-linklist">
              {selEdges.map((e: GraphEdge, i: number) => {
                const other = e.from === selected ? byId[e.to] : byId[e.from];
                return (
                  <li key={i} onClick={() => setSelected(other.id)} style={{ cursor: 'pointer' }}>
                    <i className={'bi ' + (e.from === selected ? 'bi-arrow-right' : 'bi-arrow-left')}></i>
                    <span>{other.label}</span>
                    <Badge tone="neutral">{t(e.rel)}</Badge>
                  </li>
                );
              })}
            </ul>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
